import { buttonVariants } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@arcadeai/design-system/components/ui/atoms/card";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useMemo } from "react";
import { useSuspenseGetSubscription } from "@/app/billing/api/hooks";
import type { Price } from "@/app/billing/types";
import { toFriendlyMetricName } from "@/app/billing/utils";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";

export function AllocationsWidget() {
  const { organizationId } = useOrgProjectRouteContext();
  const { data: subscription } = useSuspenseGetSubscription(organizationId);

  const { allocations, planName } = useMemo(() => {
    const plan = subscription.plan;
    const planAllocations = (plan?.prices ?? [])
      .filter(
        (price: Price) =>
          price.price_type === "fixed_price" && price.credit_allocation
      )
      .map((price: Price) => ({
        name: price.item.name,
        quantity: price.fixed_price_quantity,
        currency: price.credit_allocation?.currency,
      }));

    return { allocations: planAllocations, planName: plan?.name ?? "" };
  }, [subscription]);

  const nonZeroAllocations = allocations.filter((a) => (a.quantity ?? 0) > 0);

  const toFriendlyName = (rawName: string): string =>
    toFriendlyMetricName(rawName);

  return (
    <div className="grid grid-cols-1">
      <Card className="gap-2">
        <CardHeader className="mt-1 flex flex-row justify-between pb-2">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <CardTitle className="text-xl">Usage Allocations</CardTitle>
            </div>
            <CardDescription>
              {planName
                ? `Monthly quotas included in your ${planName} plan`
                : "Monthly quotas"}
            </CardDescription>
          </div>
          <Link
            className={cn(buttonVariants({ variant: "secondary", size: "sm" }))}
            from="/orgs/$organizationId/projects/$projectId/billing/overview"
            to="."
          >
            View details
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </CardHeader>
        <CardContent>
          {nonZeroAllocations.length === 0 ? (
            <div className="text-muted-foreground text-sm">
              No allocations available
            </div>
          ) : (
            <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {nonZeroAllocations.map((alloc) => (
                <div
                  className="flex items-center justify-between rounded-md border p-3"
                  key={`${alloc.name}-${alloc.currency ?? "na"}`}
                >
                  <div className="font-medium text-sm">
                    {toFriendlyName(alloc.name)}
                  </div>
                  <span className="font-semibold text-xl">
                    {alloc.quantity?.toLocaleString()}
                  </span>
                </div>
              ))}
              <div className="col-span-1 text-muted-foreground text-xs md:col-span-2">
                No usage yet. Start using your allocations any time.
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
